import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, AlertTriangle, Target, Download, Loader2, RotateCcw, Sparkles, Check, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SimulationReport as Report } from '@/types/evaluation.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { SessionReportHeader, VendorCard, KPIDetailGrid } from '@/components/vendor/index.ts';
import { generateReportPdf } from '@/utils/generateReportPdf.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';

export type AiAnalysisStatus = 'Pending' | 'Processing' | 'Complete' | 'Failed';

interface SimulationReportProps {
  aiAnalysisStatus?: AiAnalysisStatus;
  report?: Report;
  maestroName: string;
  maestroColor: string;
  duration: string;
  onBack: () => void;
  backLabel?: string;
  sessionDate?: string;
  onRetry?: () => void;
  retrying?: boolean;
  /** Pitch flow: start another attempt (deliberate-practice loop). */
  onNewAttempt?: () => void;
  /** Pitch flow: previous attempt's overall score (0-10) for the improvement delta. */
  previousScore?: number | null;
}

function SkeletonHeader({
  maestroName,
  maestroColor,
  duration,
  t,
}: {
  maestroName: string;
  maestroColor: string;
  duration: string;
  t: (k: string) => string;
}) {
  return (
    <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-3xl font-heading text-foreground">{t('report.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('report.sessionWith')}{' '}
          <span className="font-semibold" style={{ color: maestroColor }}>{maestroName}</span> — {duration}
        </p>
      </div>
      <div className="flex flex-col items-center rounded-xl border border-border bg-card p-5 sm:min-w-[140px]">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('report.globalScore')}
        </p>
        <div className="mt-1 h-10 w-16 animate-pulse rounded-md bg-muted" />
        <p className="mt-1 text-xs text-muted-foreground">/99</p>
      </div>
    </div>
  );
}

function SkeletonBody({ t }: { t: (k: string) => string }) {
  return (
    <>
      <div className="mb-6 rounded-xl border border-border bg-card p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-primary" />
          <div className="flex-1">
            <h2 className="mb-2 font-semibold text-foreground">{t('report.preparingTitle')}</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t('report.preparingDescription')}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-[240px_1fr]">
        <div className="h-64 rounded-xl bg-muted" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-muted" />
          ))}
        </div>
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <h2 className="mb-4 font-semibold text-foreground">{t('report.strengths')}</h2>
          <ul className="space-y-2.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500/40" />
                <div className="h-3 w-full rounded bg-muted" />
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <h2 className="mb-4 font-semibold text-foreground">{t('report.improvements')}</h2>
          <ul className="space-y-2.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500/40" />
                <div className="h-3 w-full rounded bg-muted" />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function FailedBody({
  onRetry,
  retrying,
  t,
}: {
  onRetry?: () => void;
  retrying?: boolean;
  t: (k: string) => string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center">
      <AlertTriangle className="h-10 w-10 text-red-500" />
      <h2 className="mt-4 text-lg font-semibold text-foreground">{t('report.failedTitle')}</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{t('report.failedDescription')}</p>
      {onRetry && (
        <Button onClick={onRetry} disabled={retrying} className="mt-5 gap-1.5">
          {retrying ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
          {retrying ? t('report.retryInProgress') : t('report.retry')}
        </Button>
      )}
    </div>
  );
}

export default function SimulationReport({
  aiAnalysisStatus = 'Complete',
  report,
  maestroName,
  maestroColor,
  duration,
  onBack,
  backLabel,
  sessionDate,
  onRetry,
  retrying,
  onNewAttempt,
  previousScore,
}: SimulationReportProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const resolvedBackLabel = backLabel ?? t('report.backToSimulation');

  const userName = user ? `${user.firstName} ${user.lastName}` : t('report.vendorLabel');
  const userInitials = user ? `${user.firstName[0]}${user.lastName[0]}` : 'VD';

  const handleDownloadPdf = async () => {
    if (!report) return;
    setGeneratingPdf(true);
    try {
      await generateReportPdf({
        report,
        userName,
        userInitials,
        maestroName,
        maestroColor,
        sessionDate,
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const TopBar = (
    <div className="mb-6 flex items-center justify-between">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground">
        <ArrowLeft className="size-4" />
        {resolvedBackLabel}
      </Button>
      {aiAnalysisStatus === 'Complete' && report && (
        <div className="flex items-center gap-2">
          {onNewAttempt && (
            <Button size="sm" variant="outline" onClick={onNewAttempt} className="gap-1.5">
              <RotateCcw className="size-4" />
              {t('report.retryPitch', 'Riprova il pitch')}
            </Button>
          )}
          <Button size="sm" onClick={handleDownloadPdf} disabled={generatingPdf}>
            {generatingPdf ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            {generatingPdf ? t('report.generatingPdf') : t('report.downloadPdf')}
          </Button>
        </div>
      )}
    </div>
  );

  if (aiAnalysisStatus === 'Pending' || aiAnalysisStatus === 'Processing') {
    return (
      <div className="animate-fade-in">
        {TopBar}
        <SkeletonHeader maestroName={maestroName} maestroColor={maestroColor} duration={duration} t={t} />
        <SkeletonBody t={t} />
      </div>
    );
  }

  if (aiAnalysisStatus === 'Failed') {
    return (
      <div className="animate-fade-in">
        {TopBar}
        <SkeletonHeader maestroName={maestroName} maestroColor={maestroColor} duration={duration} t={t} />
        <FailedBody onRetry={onRetry} retrying={retrying} t={t} />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="animate-fade-in">
        {TopBar}
        <SkeletonHeader maestroName={maestroName} maestroColor={maestroColor} duration={duration} t={t} />
        <SkeletonBody t={t} />
      </div>
    );
  }

  const vp = report.vendorProfile;

  const delta = typeof previousScore === 'number'
    ? toScore99(report.globalScore) - toScore99(previousScore)
    : null;

  return (
    <div className="animate-fade-in">
      {TopBar}

      {delta !== null && (
        <div
          className={`mb-4 rounded-xl border px-4 py-2.5 text-sm font-medium ${
            delta > 0
              ? 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-300'
              : delta < 0
                ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
                : 'border-border bg-muted/40 text-muted-foreground'
          }`}
        >
          {delta > 0
            ? t('report.deltaUp', { delta, defaultValue: `Migliorato di +${delta} rispetto al tentativo precedente 🎯` })
            : delta < 0
              ? t('report.deltaDown', { delta, defaultValue: `${delta} rispetto al tentativo precedente` })
              : t('report.deltaSame', 'Stesso punteggio del tentativo precedente')}
        </div>
      )}

      {vp ? (
        <SessionReportHeader
          maestroName={maestroName}
          maestroColor={maestroColor}
          duration={report.duration}
          globalScore={vp.globalScore}
        />
      ) : (
        <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-heading text-foreground">{t('report.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('report.sessionWith')}{' '}
              <span className="font-semibold" style={{ color: maestroColor }}>{maestroName}</span> — {report.duration}
            </p>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-border bg-card p-5 sm:min-w-[140px]">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('report.globalScore')}
            </p>
            <p
              className={`mt-1 text-4xl font-sans font-extrabold ${
                toScore99(report.globalScore) >= 80
                  ? 'text-green-500'
                  : toScore99(report.globalScore) >= 65
                    ? 'text-yellow-500'
                    : 'text-red-500'
              }`}
            >
              {toScore99(report.globalScore)}
            </p>
            <p className="text-xs text-muted-foreground">/99</p>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
        <div className="mb-3 flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Target className="size-5" />
          </span>
          <h2 className="font-heading text-lg text-foreground">{t('report.summary')}</h2>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">{report.summary}</p>
      </div>

      {vp && (
        <div className="mb-6 grid gap-6 lg:grid-cols-[240px_1fr]">
          <VendorCard
            name={userName}
            initials={userInitials}
            globalScore={vp.globalScore}
            categories={vp.categories}
          />
          <KPIDetailGrid categories={vp.categories} />
        </div>
      )}

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="size-5" />
            </span>
            <h2 className="font-heading text-lg text-foreground">{t('report.strengths')}</h2>
          </div>
          {report.strengths.length > 0 ? (
            <ul className="space-y-3">
              {report.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                    <Check className="size-3" strokeWidth={3} />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground/90">{s}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm italic text-muted-foreground">{t('report.noStrengths')}</p>
          )}
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <ArrowUpRight className="size-5" />
            </span>
            <h2 className="font-heading text-lg text-foreground">{t('report.improvements')}</h2>
          </div>
          {report.improvements.length > 0 ? (
            <ul className="space-y-3">
              {report.improvements.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    <ArrowUpRight className="size-3" strokeWidth={3} />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground/90">{s}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm italic text-muted-foreground">{t('report.noImprovements')}</p>
          )}
        </div>
      </div>

      {/* Pitch flow only: the AI's rewritten model version of the user's pitch. */}
      {report.improvedPitch && report.improvedPitch.trim() && (
        <div
          className="mb-6 rounded-xl border p-4 sm:p-6"
          style={{ borderColor: 'color-mix(in srgb, var(--secondary) 35%, transparent)', background: 'color-mix(in srgb, var(--secondary) 6%, transparent)' }}
        >
          <div className="mb-3 flex items-center gap-2.5">
            <Sparkles className="h-5 w-5 text-secondary" />
            <h2 className="font-semibold text-foreground">{t('report.improvedPitchTitle', 'Il tuo pitch, migliorato')}</h2>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">{t('report.improvedPitchHint', 'Una versione modello del tuo pitch, pronta da provare ad alta voce.')}</p>
          <blockquote className="whitespace-pre-line border-l-2 border-secondary/40 pl-4 font-display text-lg leading-relaxed text-foreground">
            {report.improvedPitch}
          </blockquote>
        </div>
      )}
    </div>
  );
}
